import { useState } from "react"
import { SEO } from "@/components/SEO"
import { Link, useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { motion, Variants } from "framer-motion"
import {
  Building2,
  Users,
  TrendingUp,
  Wrench,
  FileText,
  Bell,
  ShieldCheck,
  BarChart3,
  Check,
  ArrowRight,
  Zap,
  Crown,
  Gift,
  Star,
  ChevronRight,
  Wallet,
  Globe,
} from "lucide-react"

import { api } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import PublicNavbar from "@/components/shared/PublicNavbar"
import PublicFooter from "@/components/shared/PublicFooter"
import { GoogleAnalytics } from "@/components/shared/GoogleAnalytics"
import { APP_NAME, GA_ID, MARKETPLACE_ENABLED, BILLING_ENABLED, EMAIL_SUPPORT } from "@/lib/appConfig"
import { cn } from "@/lib/utils"
import { toolsRegistry, TOOL_CATEGORIES } from "@/app/tools/toolRegistry"

// ─── Types ───────────────────────────────────────────────────────────────────

interface PlanPrice {
  currency: string
  amount_monthly: string
  amount_annual: string
}

interface Plan {
  id: string
  name: string
  display_name: string
  description: string
  plan_type: string
  property_limit: number | null
  tenant_limit: number | null
  lease_limit: number | null
  document_limit: number | null
  storage_limit_gb: number | null
  features: { multi_currency: boolean; listings: boolean; api_access: boolean }
  free_trial_days: number
  prices: PlanPrice[]
}

const PLAN_RANK: Record<string, number> = { free: 0, starter: 1, pro: 2, premium: 3 }
const PLAN_ICONS: Record<string, typeof Zap> = { free: Gift, starter: Zap, pro: Building2, premium: Crown }

function formatLimit(val: number | null | undefined, unit: string): string {
  if (val === null || val === undefined) return `Unlimited ${unit}`
  return `${val} ${unit}`
}

// ─── Animations ──────────────────────────────────────────────────────────────

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
}

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function DashboardMockup() {
  return (
    <div className="relative w-full max-w-lg mx-auto">
      {/* Glow background — desktop only */}
      <div className="hidden md:block absolute inset-0 bg-gradient-to-tr from-primary/20 via-violet-500/10 to-teal-500/20 rounded-3xl blur-3xl scale-110" />

      <div className="relative bg-card border border-border/60 rounded-3xl shadow-2xl overflow-hidden p-5 md:bg-card/80 md:backdrop-blur-2xl">
        {/* App header bar */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <img src="/app-icon.png" className="h-7 w-7 object-contain" alt={APP_NAME} />
            <span className="text-xs font-bold tracking-tight">{APP_NAME.toUpperCase()}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] text-muted-foreground">Live</span>
          </div>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-3 gap-2.5 mb-4">
          {[
            { label: "Properties", value: "12", color: "bg-teal-500/15 text-teal-600 border-teal-200/60" },
            { label: "Tenants", value: "28", color: "bg-blue-500/15 text-blue-600 border-blue-200/60" },
            { label: "Revenue", value: "€8.4k", color: "bg-violet-500/15 text-violet-600 border-violet-200/60" },
          ].map((s) => (
            <div key={s.label} className={cn("rounded-2xl border p-3", s.color)}>
              <p className="text-[10px] font-medium opacity-70 uppercase tracking-wide mb-1">{s.label}</p>
              <p className="text-lg font-extrabold leading-none">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Mini chart bars */}
        <div className="bg-muted/40 rounded-2xl p-3 mb-4">
          <p className="text-[10px] text-muted-foreground font-medium mb-2.5">Monthly Revenue</p>
          <div className="flex items-end gap-1.5 h-12">
            {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 100].map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm bg-primary/60"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </div>

        {/* Tenant rows */}
        <div className="space-y-2">
          {[
            { name: "Emma Johnson", unit: "Unit 3A", status: "Active", color: "bg-emerald-400" },
            { name: "Marc Dubois", unit: "Unit 1B", status: "Active", color: "bg-emerald-400" },
            { name: "Anna Schmidt", unit: "Unit 2C", status: "Due soon", color: "bg-amber-400" },
          ].map((t) => (
            <div key={t.name} className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold">
                  {t.name[0]}
                </div>
                <div>
                  <p className="text-[11px] font-medium leading-none">{t.name}</p>
                  <p className="text-[9px] text-muted-foreground mt-0.5">{t.unit}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <div className={cn("h-1.5 w-1.5 rounded-full", t.color)} />
                <span className="text-[9px] text-muted-foreground">{t.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function HeroSection() {
  return (
    <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden">
      {/* Background gradient blobs — desktop only (expensive GPU blur on mobile) */}
      <div className="hidden md:block absolute top-0 left-1/4 h-[500px] w-[600px] bg-primary/10 rounded-full blur-[120px] -translate-y-1/2 pointer-events-none" />
      <div className="hidden md:block absolute bottom-0 right-1/4 h-[400px] w-[500px] bg-violet-500/8 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text — static (no Framer Motion) so h1 is immediately visible as LCP */}
          <div>
            <div>
              <Badge className="mb-5 bg-primary/10 text-primary border-primary/20 hover:bg-primary/15 px-3 py-1 text-xs font-semibold rounded-full">
                Property Management SaaS
              </Badge>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-5">
              Manage your{" "}
              <span className="bg-gradient-to-r from-primary via-violet-500 to-teal-500 bg-clip-text text-transparent">
                rental portfolio
              </span>{" "}
              with ease
            </h1>

            <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg">
              Track tenants, leases, finances, and maintenance — all in one beautiful dashboard.
              Built for independent European landlords who want full control without the complexity.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button size="lg" className="w-full sm:w-auto h-12 px-8 rounded-xl font-semibold text-base shadow-sm">
                  Register Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              {BILLING_ENABLED && (
                <a href="#pricing">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto h-12 px-8 rounded-xl font-semibold text-base">
                    See Pricing
                  </Button>
                </a>
              )}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Free forever &middot; No credit card required &middot; Set up in minutes
            </p>

            <div className="mt-5 flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex -space-x-2">
                {["L", "M", "S", "J"].map((i) => (
                  <div key={i} className="h-7 w-7 rounded-full bg-muted border-2 border-background flex items-center justify-center text-[10px] font-bold">
                    {i}
                  </div>
                ))}
              </div>
              <span>Trusted by <strong className="text-foreground">500+</strong> landlords</span>
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                ))}
              </div>
            </div>

            <div className="mt-4 flex items-center gap-4">
              <span className="text-[11px] font-medium text-muted-foreground/50 uppercase tracking-widest">Featured on</span>
              <a
                href="https://fazier.com/launches/rent-keep.com"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-75 hover:opacity-100 transition-opacity duration-200"
              >
                <img
                  src="https://fazier.com/api/v1//public/badges/launch_badges.svg?badge_type=launched&theme=light"
                  width={120}
                  alt="Fazier badge"
                  className="h-8 w-auto"
                />
              </a>
              <a
                href="https://peerpush.net/p/rentkeep-property-manager"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-75 hover:opacity-100 transition-opacity duration-200"
              >
                <img
                  src="https://peerpush.net/p/rentkeep-property-manager/badge.png"
                  alt="RentKeep - Property Manager badge"
                  className="h-8 w-auto"
                />
              </a>
            </div>
          </div>

          {/* Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 40, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
          >
            <DashboardMockup />
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ─── Stats Bar ────────────────────────────────────────────────────────────────

function StatsBar() {
  const stats = [
    { value: "500+", label: "Active Landlords" },
    { value: "€12M+", label: "Rent Tracked" },
    { value: "2,400+", label: "Properties Managed" },
    { value: "98%", label: "Satisfaction Rate" },
  ]
  return (
    <section className="py-10 border-y border-border/60 bg-muted/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <p className="text-3xl font-extrabold tracking-tight text-foreground">{s.value}</p>
              <p className="text-sm text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Features ─────────────────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: Building2,
    title: "Property Management",
    description: "Add and manage unlimited properties with rooms, photos, documents, and full address details.",
    color: "bg-teal-500/15 text-teal-600",
  },
  {
    icon: Users,
    title: "Tenant Profiles",
    description: "Maintain complete tenant records — contact info, ID documents, and their full lease history.",
    color: "bg-blue-500/15 text-blue-600",
  },
  {
    icon: TrendingUp,
    title: "Finance Tracking",
    description: "Record rent payments, track expenses, and generate income reports in seconds.",
    color: "bg-violet-500/15 text-violet-600",
  },
  {
    icon: Wrench,
    title: "Maintenance Requests",
    description: "Log repairs, assign priority, track status, and keep a full history of all maintenance work.",
    color: "bg-orange-500/15 text-orange-600",
  },
  {
    icon: FileText,
    title: "Document Vault",
    description: "Securely store leases, insurance certificates, inspection reports, and all key documents.",
    color: "bg-rose-500/15 text-rose-600",
  },
  {
    icon: Bell,
    title: "Smart Reminders",
    description: "Automated email reminders for rent due dates, lease renewals, and maintenance follow-ups.",
    color: "bg-amber-500/15 text-amber-600",
  },
  ...(MARKETPLACE_ENABLED ? [{
    icon: Globe,
    title: "Marketplace Listings",
    description: `List vacant properties on the ${APP_NAME} marketplace and attract tenants directly.`,
    color: "bg-cyan-500/15 text-cyan-600",
  }] : []),
  {
    icon: BarChart3,
    title: "Insights & Reports",
    description: "Visual dashboards showing cashflow, occupancy rates, and portfolio performance at a glance.",
    color: "bg-indigo-500/15 text-indigo-600",
  },
]

function FeaturesSection() {
  return (
    <section id="features" className="py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="text-center mb-14"
        >
          <motion.div variants={fadeUp}>
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 px-3 py-1 text-xs font-semibold rounded-full">
              Everything you need
            </Badge>
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
            All your landlord tools in one place
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Stop juggling spreadsheets, email threads, and paper leases. {APP_NAME} brings everything
            together so you can focus on growing your portfolio.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {FEATURES.map((f) => {
            const Icon = f.icon
            return (
              <motion.div
                key={f.title}
                variants={fadeUp}
                className="group rounded-2xl border border-border/70 bg-card p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center mb-4", f.color)}>
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-sm mb-1.5">{f.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{f.description}</p>
              </motion.div>
            )
          })}
        </motion.div>
      </div>
    </section>
  )
}

// ─── How It Works ─────────────────────────────────────────────────────────────

const STEPS = [
  {
    num: "01",
    icon: Building2,
    title: "Add your property",
    description: "Create your property with address, rooms, and photos. Takes less than 2 minutes.",
    color: "bg-teal-500/15 text-teal-600 border-teal-200/60",
  },
  {
    num: "02",
    icon: Users,
    title: "Add tenants & create leases",
    description: "Build tenant profiles and set up leases with rent amounts, payment schedules, and start/end dates.",
    color: "bg-blue-500/15 text-blue-600 border-blue-200/60",
  },
  {
    num: "03",
    icon: Wallet,
    title: "Track everything automatically",
    description: "Log rent payments, maintenance requests, and expenses. Get reminders before anything is due.",
    color: "bg-violet-500/15 text-violet-600 border-violet-200/60",
  },
]

function HowItWorksSection() {
  return (
    <section id="how-it-works" className="py-20 md:py-28 bg-muted/20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="text-center mb-14"
        >
          <motion.div variants={fadeUp}>
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 px-3 py-1 text-xs font-semibold rounded-full">
              Quick setup
            </Badge>
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
            Up and running in minutes
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
            No training needed, no complex onboarding. Add your first property, invite your tenant, and you&apos;re managing rent.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 relative"
        >
          {/* Connector line on desktop */}
          <div className="hidden md:block absolute top-12 left-[calc(16.66%-12px)] right-[calc(16.66%-12px)] h-px bg-gradient-to-r from-teal-300 via-blue-300 to-violet-300 opacity-60" />

          {STEPS.map((step) => {
            const Icon = step.icon
            return (
              <motion.div
                key={step.num}
                variants={fadeUp}
                className="relative flex flex-col items-center text-center"
              >
                <div className={cn(
                  "relative z-10 h-16 w-16 rounded-2xl border-2 flex items-center justify-center mb-5 shadow-sm",
                  step.color
                )}>
                  <Icon className="h-7 w-7" />
                  <div className="absolute -top-2.5 -right-2.5 h-6 w-6 rounded-full bg-background border-2 border-border flex items-center justify-center">
                    <span className="text-[9px] font-black text-muted-foreground">{step.num}</span>
                  </div>
                </div>
                <h3 className="font-bold text-base mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">{step.description}</p>
              </motion.div>
            )
          })}
        </motion.div>
      </div>
    </section>
  )
}

// ─── Pricing ──────────────────────────────────────────────────────────────────

function PricingSection() {
  const [annual, setAnnual] = useState(false)
  const navigate = useNavigate()

  const { data: plans = [], isLoading } = useQuery<Plan[]>({
    queryKey: ["plans-public"],
    queryFn: async () => {
      const { data } = await api.get("/api/plans")
      return (data.data ?? []).sort((a: Plan, b: Plan) => (PLAN_RANK[a.name] ?? 99) - (PLAN_RANK[b.name] ?? 99))
    },
    staleTime: 5 * 60_000,
  })

  return (
    <section id="pricing" className="py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="text-center mb-12"
        >
          <motion.div variants={fadeUp}>
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 px-3 py-1 text-xs font-semibold rounded-full">
              Transparent pricing
            </Badge>
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
            Simple plans, no surprises
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Start free, upgrade when you need more. Every paid plan includes a free trial — no credit card required.
          </motion.p>

          {/* Billing toggle */}
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-3">
            <span className={cn("text-sm font-medium", annual && "text-muted-foreground")}>Monthly</span>
            <Switch checked={annual} onCheckedChange={setAnnual} />
            <span className={cn("text-sm font-medium", !annual && "text-muted-foreground")}>Annual</span>
            {annual && (
              <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs font-semibold">Save 17%</Badge>
            )}
          </motion.div>
        </motion.div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-80 rounded-2xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : (
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {plans.map((plan) => {
              const Icon = PLAN_ICONS[plan.name] ?? Zap
              const isPro = plan.name === "pro"
              const isFree = plan.plan_type === "free"
              const eurPrice = plan.prices.find((p) => p.currency === "EUR")
              const monthlyEquiv = annual && eurPrice
                ? (parseFloat(eurPrice.amount_annual) / 12).toFixed(2)
                : eurPrice?.amount_monthly

              return (
                <motion.div
                  key={plan.id}
                  variants={fadeUp}
                  className={cn(
                    "relative flex flex-col rounded-2xl border p-6",
                    isPro
                      ? "border-primary shadow-lg shadow-primary/15 bg-primary/5"
                      : isFree
                        ? "border-border bg-muted/30"
                        : "border-border bg-card"
                  )}
                >
                  {isPro && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground px-3 py-0.5 text-xs font-semibold">
                        Most Popular
                      </Badge>
                    </div>
                  )}

                  <div className="flex items-center gap-2.5 mb-4">
                    <div className={cn(
                      "h-9 w-9 rounded-xl flex items-center justify-center",
                      isPro ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                    )}>
                      <Icon className="h-4.5 w-4.5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm leading-tight">{plan.display_name}</h3>
                      <p className="text-[11px] text-muted-foreground">{plan.description}</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-5">
                    {isFree ? (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold">Free</span>
                        <span className="text-sm text-muted-foreground">forever</span>
                      </div>
                    ) : annual && eurPrice ? (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold">€{monthlyEquiv}</span>
                          <span className="text-sm text-muted-foreground">/mo</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">Billed €{eurPrice.amount_annual}/year</p>
                      </>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold">€{eurPrice?.amount_monthly}</span>
                        <span className="text-sm text-muted-foreground">/mo</span>
                      </div>
                    )}
                    {!isFree && plan.free_trial_days > 0 && (
                      <p className="text-xs text-emerald-600 font-medium mt-1">{plan.free_trial_days}-day free trial</p>
                    )}
                  </div>

                  {/* Feature list */}
                  <ul className="flex-1 space-y-2 mb-6">
                    {[
                      formatLimit(plan.property_limit, "properties"),
                      formatLimit(plan.tenant_limit, "tenants"),
                      formatLimit(plan.lease_limit, "leases"),
                      plan.storage_limit_gb !== null ? `${plan.storage_limit_gb} GB storage` : "Unlimited storage",
                      "Rent tracking",
                      "Maintenance requests",
                      ...(MARKETPLACE_ENABLED && plan.features?.listings ? ["Marketplace listings"] : []),
                      ...(plan.features?.api_access ? ["API access"] : []),
                    ].map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs">
                        <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={cn("w-full rounded-xl text-sm font-semibold", isPro ? "" : "")}
                    variant={isPro ? "default" : "outline"}
                    onClick={() => navigate(isFree ? "/register" : "/register")}
                  >
                    {isFree ? "Get Started Free" : `Start Free Trial`}
                  </Button>
                </motion.div>
              )
            })}
          </motion.div>
        )}

        <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Secured by Stripe</div>
          <div className="flex items-center gap-1.5"><Check className="h-4 w-4 text-emerald-500" /> Cancel anytime</div>
          <div className="flex items-center gap-1.5"><Gift className="h-4 w-4 text-emerald-500" /> No credit card for free plan</div>
        </div>
      </div>
    </section>
  )
}

// ─── Testimonials ─────────────────────────────────────────────────────────────

const TESTIMONIALS = [
  {
    text: `I used to manage everything in a spreadsheet. ${APP_NAME} saved me hours every month and I never miss a rent payment now.`,
    name: "Sophie Laurent",
    role: "Landlord, 8 properties",
    initials: "SL",
    color: "bg-teal-100 text-teal-700",
  },
  {
    text: "The maintenance tracking alone is worth it. My tenants can see the status and I don't get 10 follow-up messages.",
    name: "Andreas Müller",
    role: "Portfolio Manager, Berlin",
    initials: "AM",
    color: "bg-blue-100 text-blue-700",
  },
  {
    text: "Finally a tool that feels modern. Everything is where I expect it and the mobile app is genuinely great.",
    name: "Maria Rossi",
    role: "Landlord, Milan",
    initials: "MR",
    color: "bg-violet-100 text-violet-700",
  },
]

function TestimonialsSection() {
  return (
    <section className="py-20 md:py-24 bg-muted/20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="text-center mb-12"
        >
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
            Loved by independent landlords
          </motion.h2>
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-1 mb-2">
            {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />)}
            <span className="ml-2 text-sm text-muted-foreground font-medium">4.9 / 5 average rating</span>
          </motion.div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-3 gap-5"
        >
          {TESTIMONIALS.map((t) => (
            <motion.div
              key={t.name}
              variants={fadeUp}
              className="rounded-2xl border border-border/70 bg-card p-6 flex flex-col gap-4"
            >
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1">&ldquo;{t.text}&rdquo;</p>
              <div className="flex items-center gap-3">
                <div className={cn("h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold", t.color)}>
                  {t.initials}
                </div>
                <div>
                  <p className="text-sm font-semibold leading-tight">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

// ─── Free Tools Section ───────────────────────────────────────────────────────

function FreeToolsSection() {
  const TOP_TOOLS = toolsRegistry.filter((t) => t.priority <= 6)

  return (
    <section className="py-20 md:py-24 bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="text-center mb-12"
        >
          <motion.div variants={fadeUp}>
            <Badge className="mb-3">Free Landlord Tools</Badge>
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
            Tools every landlord needs — free, forever
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground text-lg max-w-xl mx-auto mb-5">
            No sign-up. No login. Just instant answers to your most important property questions.
          </motion.p>
          <motion.div variants={fadeUp}>
            <Link to="/tools" className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1">
              Browse all 21 free tools <ChevronRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {TOP_TOOLS.map((tool) => {
            const catColor = TOOL_CATEGORIES[tool.category].color
            return (
              <motion.div key={tool.slug} variants={fadeUp}>
                <Link to={`/tools/${tool.slug}`} className="block h-full">
                  <div className="rounded-2xl border border-border/70 bg-card p-5 hover:shadow-md hover:-translate-y-0.5 transition-all group h-full flex flex-col">
                    <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center mb-3 flex-shrink-0", catColor)}>
                      <tool.icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1 leading-snug">{tool.shortTitle}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-3 flex-1">{tool.description}</p>
                    <span className="text-xs font-semibold text-primary group-hover:underline mt-auto">
                      Open tool →
                    </span>
                  </div>
                </Link>
              </motion.div>
            )
          })}
        </motion.div>

        <div className="text-center mt-8">
          <Link to="/tools">
            <Button variant="outline" className="rounded-xl">
              See all 21 free tools
              <ChevronRight className="ml-1.5 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  )
}

// ─── CTA Banner ───────────────────────────────────────────────────────────────

function CTASection() {
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="relative overflow-hidden rounded-3xl bg-foreground p-10 md:p-16 text-center"
        >
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-violet-500/20 to-teal-500/20 opacity-60" />
          <div className="absolute -top-24 -right-24 h-64 w-64 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-64 w-64 bg-violet-500/20 rounded-full blur-3xl" />

          <div className="relative z-10">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
              Ready to simplify your
              <br />
              landlord life?
            </h2>
            <p className="text-lg text-white/70 mb-8 max-w-xl mx-auto">
              Join 500+ landlords already using {APP_NAME}. Free forever plan available — no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register">
                <Button size="lg" className="h-12 px-10 rounded-xl font-semibold text-base bg-white text-foreground hover:bg-white/90 shadow-lg">
                  Get Started Free
                  <ChevronRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="ghost" className="h-12 px-8 rounded-xl font-semibold text-base text-white hover:bg-white/10 border border-white/20">
                  Sign In
                </Button>
              </Link>
            </div>
            <p className="mt-5 text-sm text-white/50">
              Free plan • No credit card • Cancel anytime
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LandingPage() {


  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={`${APP_NAME} — Property Management for Independent Landlords`}
        description={`Free property management software for independent landlords. Track rent, tenants, leases, maintenance, and finances in one place with ${APP_NAME}.`}
        canonicalUrl="https://rent-keep.com/"
        jsonLd={[
          {
            "@type": "SoftwareApplication",
            "@id": "https://rent-keep.com/#software",
            "name": APP_NAME,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web, Android",
            "url": "https://rent-keep.com",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Rent tracking",
              "Tenant management",
              "Lease management",
              "Maintenance tracking",
              "Expense and income reporting",
              "Document storage",
            ],
            "description": "Free property management software for independent landlords."
          },
          {
            "@type": "Organization",
            "@id": "https://rent-keep.com/#organization",
            "name": APP_NAME,
            "url": "https://rent-keep.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://rent-keep.com/app-icon.png",
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer support",
              "email": EMAIL_SUPPORT,
            },
          },
          {
            "@type": "WebSite",
            "@id": "https://rent-keep.com/#website",
            "name": APP_NAME,
            "url": "https://rent-keep.com",
            "publisher": { "@id": "https://rent-keep.com/#organization" },
          },
        ]}
      />
      <GoogleAnalytics id={GA_ID} />
      <PublicNavbar />
      <main>
        <HeroSection />
        <StatsBar />
        <FeaturesSection />
        <HowItWorksSection />
        {BILLING_ENABLED && <PricingSection />}
        <TestimonialsSection />
        <FreeToolsSection />
        <CTASection />
      </main>
      <PublicFooter />
    </div>
  )
}
